import { FC, useState, useEffect } from "react";
import { Box } from "@mui/material";
import ImagePreviewModal from "@/components/UI/ImagePreviewModal/ImagePreviewModal";

interface PostImagesGridProps {
  attachments: { url: string; mimeType: string; uid: string }[];
}

const PostImagesGrid: FC<PostImagesGridProps> = ({ attachments }) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [ratios, setRatios] = useState<Record<string, number>>({});
  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
    setOpen(true);
  };
  const count = attachments.length;
  const getOrientation = (width: number, height: number): "vertical" | "horizontal" => {
    return height >= width ? "vertical" : "horizontal";
  };

  const [orientations, setOrientations] = useState<("vertical" | "horizontal")[]>([]);

  useEffect(() => {
    const loadImageData = async () => {
      const promises = attachments.map(
        (att) =>
          new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            img.src = att.url;
            img.onload = () => resolve({ width: img.width, height: img.height });
          })
      );

      const sizes = await Promise.all(promises);
      setOrientations(sizes.map((s) => getOrientation(s.width, s.height)));
    };

    loadImageData();
  }, [attachments]);

  useEffect(() => {
    attachments.forEach((att) => {
      const img = new Image();
      img.src = att.url;
      img.onload = () => {
        setRatios((prev) => ({
          ...prev,
          [att.uid]: img.naturalWidth / img.naturalHeight,
        }));
      };
    });
  }, [attachments]);

  const allLoaded = attachments.every((att) => ratios[att.uid]);

  if (count === 1) {
    return (
      <>
        <Box
          sx={{
            maxHeight: "26.875rem",
            borderRadius: "1rem",
            overflow: "hidden",
            mt: 2,
          }}
        >
          <Box
            onClick={() => handleImageClick(0)}
            sx={{ cursor: "pointer", width: "100%", height: "100%" }}
          >
            <img
              src={attachments[0].url}
              alt="Post image"
              style={{
                width: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        <ImagePreviewModal
          images={attachments.map((a) => a.url)}
          open={open}
          onClose={() => setOpen(false)}
          initialIndex={selectedIndex}
        />
      </>
    );
  }

  if (count === 2 && orientations.length === 2) {
    const [first, second] = orientations;

    let gridTemplateColumns = "1fr 1fr";
    let gridTemplateRows = "1fr";
    let gridItems: { gridColumn?: string; gridRow?: string }[] = [{}, {}];

    if (first === "vertical" && second === "vertical") {
      gridTemplateColumns = "1fr 1fr";
      gridTemplateRows = "1fr 1fr";
      gridItems = [
        { gridColumn: "1", gridRow: "1 / span 2" },
        { gridColumn: "2", gridRow: "1 / span 2" },
      ];
    } else if (first === "horizontal" && second === "horizontal") {
      gridTemplateColumns = "1fr 1fr";
      gridTemplateRows = "1fr 1fr";
      gridItems = [
        { gridColumn: "1 / span 2", gridRow: "1" },
        { gridColumn: "1 / span 2", gridRow: "2" },
      ];
    } else {
      gridTemplateColumns = "repeat(4, 1fr)";
      gridTemplateRows = "1fr";

      if (first === "vertical" && second === "horizontal") {
        gridItems = [
          { gridColumn: "1 / span 1", gridRow: "1" },
          { gridColumn: "2 / span 3", gridRow: "1" },
        ];
      } else if (first === "horizontal" && second === "vertical") {
        gridItems = [
          { gridColumn: "1 / span 3", gridRow: "1" },
          { gridColumn: "4 / span 1", gridRow: "1" },
        ];
      }
    }

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns,
            gridTemplateRows,
            gap: "0.5rem",
            mt: 2,
            height: "26.875rem",
          }}
        >
          {attachments.map((att, index) => (
            <Box
              key={att.uid}
              sx={{
                borderRadius: "1rem",
                overflow: "hidden",
                cursor: "pointer",
                ...gridItems[index],
              }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={att.url}
                alt="Post image"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          ))}
        </Box>

        <ImagePreviewModal
          images={attachments.map((a) => a.url)}
          open={open}
          onClose={() => setOpen(false)}
          initialIndex={selectedIndex}
        />
      </>
    );
  }

  if (count === 3) {
    if (!allLoaded) {
      return (
        <Box
          sx={{
            height: "26.875rem",
            borderRadius: "1rem",
            overflow: "hidden",
            mt: 2,
            bgcolor: "grey.100",
          }}
        />
      );
    }

    const classified = attachments.map((att) => {
      const ratio = ratios[att.uid];
      if (ratio > 1.3) return { ...att, type: "horizontal" };
      if (ratio < 0.77) return { ...att, type: "vertical" };
      return { ...att, type: "square" };
    });

    const extreme = classified.reduce((acc, curr) => {
      const ratio = ratios[curr.uid];
      const accRatio = ratios[acc.uid];
      const currDeviation = Math.abs(Math.log(ratio));
      const accDeviation = Math.abs(Math.log(accRatio));
      return currDeviation > accDeviation ? curr : acc;
    }, classified[0]);

    const isVerticalDominant = extreme.type === "vertical";
    const extremeIndex = attachments.findIndex((a) => a.uid === extreme.uid);

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            gap: "0.5rem",
            mt: 2,
            height: "26.875rem",
          }}
        >
          {isVerticalDominant ? (
            <>
              {attachments.map((att, index) =>
                index === extremeIndex ? (
                  <Box
                    key={att.uid}
                    sx={{
                      gridColumn: "2 / 3",
                      gridRow: "1 / 3",
                      borderRadius: "1rem",
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={att.url}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    key={att.uid}
                    sx={{
                      borderRadius: "1rem",
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={att.url}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )
              )}
            </>
          ) : (
            <>
              {attachments.map((att, index) =>
                index === extremeIndex ? (
                  <Box
                    key={att.uid}
                    sx={{
                      gridColumn: "1 / 3",
                      gridRow: "2 / 3",
                      borderRadius: "1rem",
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={att.url}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    key={att.uid}
                    sx={{
                      borderRadius: "1rem",
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={att.url}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )
              )}
            </>
          )}
        </Box>

        <ImagePreviewModal
          images={attachments.map((a) => a.url)}
          open={open}
          onClose={() => setOpen(false)}
          initialIndex={selectedIndex}
        />
      </>
    );
  }

  if (count === 4) {
    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            gap: "0.5rem",
            mt: 2,
            height: "26.875rem",
          }}
        >
          {attachments.slice(0, 4).map((att, index) => (
            <Box
              key={att.uid}
              sx={{
                borderRadius: "1rem",
                overflow: "hidden",
                cursor: "pointer",
              }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={att.url}
                alt="Post image"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ))}
        </Box>

        <ImagePreviewModal
          images={attachments.map((a) => a.url)}
          open={open}
          onClose={() => setOpen(false)}
          initialIndex={selectedIndex}
        />
      </>
    );
  }

  return null;
};

export default PostImagesGrid;
